import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import { useFetchOperatorTableData } from "../../hooks/use-fetch-operator-table-data";
import {
  operatorRowSortTime,
  computeOperatorColumnWidths,
} from "../../utils/TableHelpers/operatorTableData";
import { AuthContext } from "../../utils/AuthContext";
import { ClipLoader } from "react-spinners";
import PropTypes from "prop-types";
import styled from "styled-components";
import "./OperatorFixedRouteView.css";

// Special-service rows are tinted blue (distinct from the red/orange pullout-time
// states used in the main table) with a matching legend swatch.
const SPECIAL_ROW_BG = "#e6f0ff";
const SPECIAL_ROW_BORDER = "#2563eb";

// Styles for the loading spinner
const spinnerOverrideStyles = {
  margin: "0 auto",
  textAlign: "center",
  top: "45%",
  left: "50%",
  position: "absolute",
};

const TableContainer = styled.div`
  flex: 1;
  /* Wrapped special rows can outgrow the kiosk viewport; keep the nav controls reachable. */
  overflow-y: auto;

  table {
    table-layout: fixed;
    width: 100%;
  }

  /* Global \`table { white-space: nowrap }\` plus table-layout:fixed let long
     special-service values paint over the next column; wrap them instead. */
  td {
    white-space: normal;
  }

  /* Last resort for values with no space to wrap at, e.g. special run numbers.
     Scoped to the body so headers never break mid-word. */
  tbody td {
    overflow-wrap: break-word;
  }

  thead td {
    background-color: #eee;
    color: var(--clr-grey-2);
    font-weight: 600;
  }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 4px 0 0 20px;
  font-size: 13px;
  color: #555;
`;

const BottomControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: 0 auto;
  padding: 6px 0 10px;
  gap: 8px;
  width: fit-content;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
  }

  input {
    width: 150px;
    padding: 5px 10px;
    font-size: 15px;
    border: 2px solid #ccc;
    border-radius: 4px;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #B00C28;
    }
  }
`;

const NavigationRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const ArrowButton = styled.button`
  flex: 1;
  background: #B00C28;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 7px 0;
  font-size: 15px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #86091E;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

// Parses out the SignUpTimeFmt string and returns the time in milliseconds
const parseTimeString = (timeString) => {
  if (!timeString) {
    return 0;
  }
  const [time, ampm] = timeString.split(" ");
  const [hours, minutes] = time.split(":").map(Number);
  const adjustedHours = ampm === "X" ? hours + 24 : hours;
  let milliseconds =
    (adjustedHours % 24) * 60 * 60 * 1000 + minutes * 60 * 1000;
  if (ampm === "P" && hours !== 12) {
    milliseconds += 12 * 60 * 60 * 1000;
  }
  // Adjust times after midnight to be after the next day
  if (adjustedHours >= 24) {
    milliseconds += 24 * 60 * 60 * 1000;
  }
  return milliseconds;
};

// Specials show their specialName in the Route column; fixed rows show the route
// abbreviation. Falls back to the route abbr when a special has no name.
const routeLabel = (row) => {
  if (!row.isSpecial) {
    return row.primaryRouteAbbr;
  }
  return (
    row.specialName?.trim() || row.pulloutRouteAbbr || row.primaryRouteAbbr
  );
};

// One source of truth for the board's columns, so the two side-by-side tables and
// their rows cannot drift apart.
const COLUMNS = [
  { key: "box", header: "Box", read: (row) => row.boxNumber },
  { key: "run", header: "Run", read: (row) => row.runNumber },
  { key: "route", header: "Route", read: routeLabel },
  { key: "block", header: "Block", read: (row) => row.blockNumber },
  {
    key: "signupTime",
    header: (
      <>
        Signup <br /> Time
      </>
    ),
    read: operatorRowSortTime,
  },
  { key: "bus", header: "Bus", read: (row) => row.lastVehicle },
  { key: "loc", header: "Loc", read: (row) => row.pulloutLocation },
  { key: "exc", header: "Exc", read: (row) => row.pulloutVarianceReason },
  { key: "note", header: "Operator Note", read: (row) => row.noteToOperator },
];

const TableHead = ({ widths }) => (
  <thead>
    <tr>
      {COLUMNS.map((column) => (
        <td key={column.key} width={`${widths[column.key]}%`}>
          {column.header}
        </td>
      ))}
    </tr>
  </thead>
);

TableHead.propTypes = {
  widths: PropTypes.objectOf(PropTypes.number).isRequired,
};

const OperatorFixedRouteView = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortedData, setSortedData] = useState([]);
  const [currentTimes, setCurrentTimes] = useState([]);
  const [boxSearchValue, setBoxSearchValue] = useState("");
  const [startIndex, setStartIndex] = useState(0);
  const intervalIdRef = useRef(null);
  const currentTimeIndexRef = useRef(0);
  const fetchOperatorTableData = useFetchOperatorTableData();
  const { operatorDivision: pulloutDivision } = useContext(AuthContext);

  // Fetch merged fixed + special data and sort by signup time (specials
  // interlace by their Time Out via operatorRowSortTime).
  const getData = useCallback(async () => {
    setError(null);
    setLoading(true);
    const response = await fetchOperatorTableData(
      new Date(),
      "ALL",
      pulloutDivision
    );
    if (response.success) {
      const sorted = [...response.data].sort(
        (a, b) =>
          parseTimeString(operatorRowSortTime(a)) -
          parseTimeString(operatorRowSortTime(b))
      );
      setSortedData(sorted);
      setLoading(false);
    } else {
      setLoading(false);
      setSortedData([]);
      setError("An error occurred while fetching table data.");
    }
  }, [fetchOperatorTableData, pulloutDivision]);

  // Fetch data on initial render and every 5 minutes
  useEffect(() => {
    getData();

    // Refresh data every 5 minutes
    const intervalId = setInterval(() => {
      getData();
    }, 300000);

    return () => {
      clearInterval(intervalId);
    };
  }, [getData]);

  const adjustHours = (hours, ampm) => {
    if (ampm === "P") {
      return hours === 12 ? 12 : hours + 12;
    } else {
      return hours;
    }
  };

  const calculateRowMilliseconds = (adjustedHours, minutes) => {
    return (adjustedHours * 60 * 60 + minutes * 60) * 1000;
  };

  // Update display from a given starting index
  const updateDisplayFromIndex = useCallback(
    (index) => {
      const newCurrentTimes = sortedData.slice(index, index + 42);
      setCurrentTimes(newCurrentTimes);
      setStartIndex(index);
    },
    [sortedData]
  );

  // Set the table display based on current time to allow for rolling display
  const setCurrentDisplay = useCallback(() => {
    setBoxSearchValue("");
    document.getElementById("operatorViewBoxSearch")?.blur();
    const currentTime = new Date().toLocaleTimeString("en-US", {
      timeZone: "America/Denver",
    });
    const [localTime, localAMPM] = currentTime.split(" ");
    const [localHours, localMinutes] = localTime.split(":").map(Number);
    let adjustedLocalHours;

    if (localAMPM === "PM") {
      adjustedLocalHours = localHours === 12 ? 12 : localHours + 12;
    } else {
      adjustedLocalHours = localHours;
    }

    const currentMilliseconds =
      (adjustedLocalHours * 60 * 60 + localMinutes * 60) * 1000;

    // Find the starting index for rows at or after current time
    let foundIndex = 0;
    for (let i = 0; i < sortedData.length; i++) {
      const row = sortedData[i];
      const rowTime = operatorRowSortTime(row);
      if (!rowTime) {
        continue;
      }

      const [time, ampm] = rowTime.split(" ");
      const [hours, minutes] = time.split(":").map(Number);
      const adjustedHours = adjustHours(hours, ampm);
      const rowMilliseconds = calculateRowMilliseconds(adjustedHours, minutes);

      if (rowMilliseconds >= currentMilliseconds) {
        foundIndex = i;
        break;
      }
    }

    // Store the current time index so we can return to it
    currentTimeIndexRef.current = foundIndex;
    updateDisplayFromIndex(foundIndex);
  }, [sortedData, updateDisplayFromIndex]);

  // Navigation handlers - always show full 42 rows when navigating
  const ROWS_PER_PAGE = 42;

  const handlePrevious = useCallback(() => {
    const newIndex = Math.max(0, startIndex - ROWS_PER_PAGE);
    const newCurrentTimes = sortedData.slice(
      newIndex,
      newIndex + ROWS_PER_PAGE
    );
    setCurrentTimes(newCurrentTimes);
    setStartIndex(newIndex);
    // Reset the auto-refresh timer when manually navigating
    clearInterval(intervalIdRef.current);
    intervalIdRef.current = setInterval(() => {
      setCurrentDisplay();
    }, 30000);
  }, [startIndex, sortedData, setCurrentDisplay]);

  const handleNext = useCallback(() => {
    const newIndex = startIndex + ROWS_PER_PAGE;

    // If navigating from before current time and reaching it, snap to current view
    if (startIndex < currentTimeIndexRef.current && newIndex >= currentTimeIndexRef.current) {
      setCurrentDisplay();
      return;
    }

    // Otherwise paginate forward normally
    const newCurrentTimes = sortedData.slice(newIndex, newIndex + ROWS_PER_PAGE);
    setCurrentTimes(newCurrentTimes);
    setStartIndex(newIndex);
    // Reset the auto-refresh timer when manually navigating
    clearInterval(intervalIdRef.current);
    intervalIdRef.current = setInterval(() => {
      setCurrentDisplay();
    }, 30000);
  }, [startIndex, sortedData, setCurrentDisplay]);

  // Set the current display on initial render and every 30 seconds
  useEffect(() => {
    setCurrentDisplay();

    // Refresh current display every 30 seconds
    intervalIdRef.current = setInterval(() => {
      setCurrentDisplay();
    }, 30000);

    return () => {
      clearInterval(intervalIdRef.current);
    };
  }, [setCurrentDisplay]);

  // Keyboard navigation with arrow keys
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't trigger if user is typing in an input field
      if (event.target.tagName === "INPUT" || event.target.tagName === "SELECT") {
        return;
      }

      if (event.key === "ArrowLeft" && startIndex > 0) {
        handlePrevious();
      } else if (event.key === "ArrowRight" && startIndex + currentTimes.length < sortedData.length) {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handlePrevious, handleNext, startIndex, currentTimes.length, sortedData.length]);

  // Split current times into two halves
  const halfLength = Math.ceil(currentTimes.length / 2);
  const firstHalf = currentTimes.slice(0, halfLength);
  const secondHalf = currentTimes.slice(halfLength);

  // Column widths come from the whole board, not the visible page: the display
  // auto-advances every 30s, and per-page widths would make the columns jump.
  const columnWidths = useMemo(
    () => computeOperatorColumnWidths(sortedData),
    [sortedData]
  );

  // Generate a single time-table row. Special-service rows are tinted and show
  // their specialName (when present) in the Route column; specials often have no
  // box number, so the Box cell is simply left blank.
  const renderRow = (row, index) => {
    const zebra = index % 2 === 0 ? "#f8f8f8" : "#f0f0f0";
    return (
      <tr
        key={`${row.isSpecial ? "sp" : "fx"}-${row.boxNumber ?? "x"}-${row.blockNumber ?? "x"}-${index}`}
        style={{
          backgroundColor: row.isSpecial ? SPECIAL_ROW_BG : zebra,
          border: row.isSpecial ? `solid 2px ${SPECIAL_ROW_BORDER}` : undefined,
          fontSize: "16px",
        }}
        height="35"
      >
        {COLUMNS.map((column) => (
          <td key={column.key} width={`${columnWidths[column.key]}%`}>
            {column.read(row)}
          </td>
        ))}
      </tr>
    );
  };

  const firstHalfRows = firstHalf.map(renderRow);
  const secondHalfRows = secondHalf.map(renderRow);

  // Handle box search input change and update current display if box number is found
  const handleBoxSearchChange = (event) => {
    const rawValue = event.target.value;
    // Only allow digits
    if (rawValue !== "" && !/^\d+$/.test(rawValue)) return;
    const value = Number(rawValue);
    const backspace = event.nativeEvent.inputType === "deleteContentBackward";
    if (backspace) {
      setCurrentDisplay();
    }
    if (value >= 0 && value < 999) {
      setBoxSearchValue(rawValue);
      const newStartingIndex = sortedData.findIndex(
        (row) => row.boxNumber === value.toString()
      );
      if (newStartingIndex !== -1) {
        updateDisplayFromIndex(newStartingIndex);
        // Reset current display refresh timer
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = setInterval(() => {
          setCurrentDisplay();
        }, 30000);
      }
    }
  };

  if (loading) {
    return (
      <ClipLoader
        color="gray"
        loading={loading}
        size={100}
        cssOverride={spinnerOverrideStyles}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 56px - var(--banner-height, 0px))" }}>
      {sortedData.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Legend>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span
                style={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "3px",
                  backgroundColor: SPECIAL_ROW_BG,
                  border: `2px solid ${SPECIAL_ROW_BORDER}`,
                  display: "inline-block",
                }}
              />
              {"Special Service"}
            </span>
            <span>{" | "}</span>
            <strong>LOOK</strong>{" = Off Site Parking; See Operator Note"}
            <span>{" | "}</span>
            <strong>NRR</strong>{" = No Run Required"}
            <span>{" | "}</span>
            <strong>NDA</strong>{" = No Driver Available"}
            <span>{" | "}</span>
            <strong>NBA</strong>{" = No Bus Available"}
          </Legend>
          <TableContainer>
            <div style={{ display: "flex", alignItems: "start" }}>
              <table style={{ margin: "0 20px" }}>
                <TableHead widths={columnWidths} />
                <tbody>
                  {firstHalfRows}
                </tbody>
              </table>
              {secondHalfRows.length > 0 && (
                <table style={{ margin: "0 20px" }}>
                  <TableHead widths={columnWidths} />
                  <tbody>
                    {secondHalfRows}
                  </tbody>
                </table>
              )}
            </div>
          </TableContainer>
          <BottomControls>
            <SearchRow>
              <label htmlFor="operatorViewBoxSearch">Box Search</label>
              <input
                id="operatorViewBoxSearch"
                onChange={handleBoxSearchChange}
                placeholder="Search by box"
                type="text"
                inputMode="numeric"
                value={boxSearchValue}
              />
            </SearchRow>
            <NavigationRow>
              <ArrowButton onClick={handlePrevious} disabled={startIndex === 0}>
                ← Back
              </ArrowButton>
              <ArrowButton
                onClick={handleNext}
                disabled={startIndex + currentTimes.length >= sortedData.length}
              >
                Forward →
              </ArrowButton>
            </NavigationRow>
          </BottomControls>
        </div>
      ) : (
        <div style={{ margin: "0 20px" }}>
          {error ? error : "No data available"}
        </div>
      )}
    </div>
  );
};

export default OperatorFixedRouteView;
